<script lang="ts">
    import { TRANSITION_DELAY } from '$lib/global/constants';
    import { PREVIEW_MODE } from '$lib/global/constants';

    import { page } from '$app/stores';
    import AffiliateBadge from '$lib/components/AffiliateBadge.svelte';
    import CookieOverlay from '$lib/components/ConsentOverlay/ConsentOverlay.svelte';
    import CustomHeader from '$lib/components/CustomHeader/CustomHeader.svelte';
    import Footer from '$lib/components/Footer/Footer.svelte';
    import Header from '$lib/components/Header.svelte';
    import ProgressBar from '$lib/components/Progressbar.svelte';
    import { debugTracking } from '$lib/debug';
    import PS_TRACKING from '$lib/global/core/ps-integration';
    import '../app.css';
    import loadAnalytics from '$lib/global/events/loadAnalytics';
    import { setGlobals } from '$lib/global/globals';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { getUserId } from '$lib/global/storage';
    import { integrations } from '$lib/global/tracking/integrations';
    import { campaignClose } from '$lib/global/tracking/v4';
    import { getFontName } from '$lib/helper';
    import BrowserStorage from '$lib/Stores/BrowserStorage.svelte';
    import { embedOptions, quizMetrics, media } from '$lib/Stores/stores';
    import { createGoToUrlWithPreservedQueries } from '$lib/utils/createGoToUrlWithPreservedQueries';
    import { handleViewportHeight } from '$lib/utils/handleViewportHeight';
    import { reportEmbedHeight } from '$lib/utils/reportEmbedHeight';

    import { init } from 'emoji-mart';
    import { onMount } from 'svelte';
    import { ModalContainer } from 'svelte-promise-modals';

    import type { LayoutData } from '$lib/types/LayoutData';
    import type { QuizMetric } from '$lib/types/QuizMetrics';
    import type { Theme } from '$lib/types/Theme';

    import 'svelte-promise-modals/style.css';

    // from +layout.js
    export let data: LayoutData;

    let analyticsScaffoldingDone = false;
    let mounted = false;

    const fontName = getFontName(data.theme as Theme);

    handleViewportHeight();

    const loadQuizMetrics = async () => {
        try {
            const response = await fetch(data.quizDataUrl);
            const metrics = (await response.json()) as QuizMetric[];

            if (metrics) {
                quizMetrics.set(metrics);
            }
        } catch (err) {
            console.warn('Error fetching Quiz metrics', {
                url: data.quizDataUrl,
                error: err,
            });
        }
    };

    const initMediaStore = () => {
        // Create object with mediaId: mediaData pairs
        const mediaMap = data.media.reduce((acc, mediaItem) => {
            acc[mediaItem._id] = mediaItem;
            return acc;
        }, {});

        media.set(mediaMap);
    };

    onMount(() => {
        if (PREVIEW_MODE) {
            const nextPageNavigator = new NextPageNavigator(
                $page.data.campaignId,
                $page.data.firstResultPage,
                $page.data.pageIdSlugMap,
                $page.data.trackingProperties,
            );

            window.perspective = {
                ...window.perspective,
                navigateToPageBySlug: (pageSlug: string) => {
                    nextPageNavigator.go(pageSlug);
                },
            };
        }

        // Init Emojimart
        init({ data: data.emojiData });

        // Reliable enough for now
        window.addEventListener('beforeunload', () => {
            campaignClose();
            setTimeout(() => null, 500);
        });

        // Tracking
        setGlobals({
            perspectiveSessionId: window.sessionId,
            anonymousId: getUserId(),
            localId: window.sessionId,
            globalId: getUserId(),
            trackingVersion: data?.context?.trackingVersion,
        });

        window.PS_TRACKING = PS_TRACKING;
        // used for storing events before cookies are accepted
        window.ps_queue = [];

        const analytics = (window.analytics = window.analytics || []);
        const analytics_queue = (window.analytics_queue = window.analytics_queue || []);

        if (!analytics.initialize) {
            debugTracking('-> Initializing Analytics');

            if (analytics.invoked) {
                window.console &&
                    console.error &&
                    console.error('Tracking snippet included twice.');
            } else {
                analytics.invoked = !0;
                analytics.methods = [
                    'trackSubmit',
                    'trackClick',
                    'trackLink',
                    'trackForm',
                    'pageview',
                    'identify',
                    'reset',
                    'group',
                    'track',
                    'ready',
                    'alias',
                    'debug',
                    'page',
                    'once',
                    'off',
                    'on',
                ];

                analytics.factory = function (method: any) {
                    return function () {
                        const args = Array.prototype.slice.call(arguments);
                        args.unshift(method);
                        analytics_queue.push(args);
                        window.ps_queue.push(JSON.parse(JSON.stringify(args)));

                        return analytics;
                    };
                };

                for (let i = 0; i < analytics.methods.length; i++) {
                    const method = analytics.methods[i];
                    analytics[method] = analytics.factory(method);
                }
            }

            analyticsScaffoldingDone = true;

            const perspectiveOnly = {
                Perspective: !!integrations.perspective,
            };

            // always load tracking with only perspective enabled
            loadAnalytics({
                companyId: data.companyId,
                integrations: perspectiveOnly,
                preventTracking: data.analytics.preventSegment,
                analyticsLink: data.analytics.analyticsLink,
            });
        } else {
            analyticsScaffoldingDone = true;
        }

        if ($embedOptions.autoHeight) {
            reportEmbedHeight();
        }

        // Fetch Quiz reveal metrics
        if (data.hasQuizReveal) {
            void loadQuizMetrics();
        }

        // Init media store
        if (data.media) {
            initMediaStore();
        }

        mounted = true;
    });

    const handleLogoClick = () => {
        if (PREVIEW_MODE) {
            const gotoUrl = new URL(data.homeLink, window.location.origin);
            const { url } = createGoToUrlWithPreservedQueries(gotoUrl.toString());
            window.location.replace(url);
            return;
        }
        window.location.replace(data.homeLink);
    };
</script>

<svelte:head>
    <meta name="theme-color" content={data.theme.backgroundColor} />
    <!-- Extends styles to apply to <div id='#perspective-frame-content'/> part of Preview feature -->
    {@html `<style>
        body, html, #perspective-frame-content {
            background-color: ${data.theme.backgroundColor};
            ${$embedOptions.autoHeight ? 'overflow: hidden;' : ''}
        }
    </style>`}
</svelte:head>

<div id="ps-cookie-banner-wrapper" class="overflow-hidden">
    {#if analyticsScaffoldingDone && $embedOptions.cookieBanner}
        <CookieOverlay
            layout={data.cookieOverlay.layout}
            language={data.cookieOverlay.language}
            integrations={data.integrations}
            legalLink={data.footer.legalLink}
            termsLink={data.footer.termsLink}
            campaignId={data.campaignId}
            campaignVersionId={data.campaignVersionId}
        />
    {/if}
</div>

<!-- Don't add any parent to this element because the preview app styles are limited to #funnel-app-main selector -->
<main
    id="funnel-app-main"
    style="--font-family: 'Twemoji Country Flags', '{fontName}'"
    class="fun-flex fun-min-h-screen fun-flex-col"
>
    {#if $embedOptions.header}
        {#if data.customHeader && data.customHeader.length > 0}
            <CustomHeader
                bgColor={data.header.bgColor}
                fixed={data.header.fixed}
                translucent={data.header.translucent}
                components={data.customHeader}
                align={data.header.align}
                onLogoClick={handleLogoClick}
                hidden={data.header.hidden}
            />
        {:else}
            <Header
                src={data.header.logo}
                align={data.header.align}
                size={data.header.size}
                onClick={handleLogoClick}
                bgColor={data.header.bgColor}
                showIcon={data.header.showIcon}
            />
        {/if}
    {/if}

    {#if mounted}
        <div class="fun-flex-1">
            <slot />
        </div>
    {/if}

    {#if $embedOptions.footer}
        <Footer
            campaignId={data.campaignId}
            campaignVersionId={data.campaignVersionId}
            cookieOverlay={data.cookieOverlay}
            integrations={data.integrations}
            legalLink={data.footer.legalLink}
            legalText={data.footer.legalText}
            termsLink={data.footer.termsLink}
            termsText={data.footer.termsText}
            language={data.cookieOverlay.language}
            bgColorReference={!data?.footer?.box?.backgroundColor ||
            data?.footer?.box?.backgroundColor === 'transparent'
                ? data.theme.backgroundColor
                : data?.footer?.box?.backgroundColor}
            branding={data.footer.branding}
            transitionDelay={$page?.data?.components?.length * TRANSITION_DELAY}
            transitionEffect={data.theme.transition}
            box={data.footer.box}
        />
    {/if}

    {#if data.showProgressBar}
        <ProgressBar
            sortedPages={data.sortedPages}
            pageId={$page.data.pageId}
            color={data.theme.buttonBackgroundColor}
            hasResultPages={data.hasResultPages}
        />
    {/if}

    {#if data.showAffiliateBadge}
        <AffiliateBadge
            link={$page.data.affiliateLink}
            campaignId={$page.data.campaignId}
            companyId={$page.data.companyId}
        />
    {/if}
</main>

<BrowserStorage campaignId={data.campaignId} resultPages={data.resultPages} />

<ModalContainer />

<style>
    main {
        font-family:
            var(--font-family),
            ui-sans-serif,
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            'Segoe UI',
            Roboto,
            'Helvetica Neue',
            Arial,
            'Noto Sans',
            sans-serif,
            'Apple Color Emoji',
            'Segoe UI Emoji',
            'Segoe UI Symbol',
            'Noto Color Emoji';
    }
</style>
